// src/pages/shipments/ShipmentDrawer.tsx
import React, { useState, useEffect, useMemo } from 'react';
import GenericDialog from 'src/components/GenericDialog';
import DynamicFormWidget from 'src/components/dynamic-widget/DynamicFormWidget';
import { DynamicField, DynamicFieldTypes } from 'src/components/dynamic-widget';
import { toast } from 'react-toastify';
import { FileType } from 'src/models/file';
import { DataService } from 'src/services/data-service';
import { LovService, LovDictionary } from 'src/services/lov-service';
import CloseIcon from '@mui/icons-material/Close';
import Loader from 'src/components/Loader';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MapIcon from '@mui/icons-material/Map';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  alpha
} from '@mui/material';
import * as XLSX from 'xlsx';
import { Autocomplete, TextField } from '@mui/material';
import { validateExcelDuplicates } from 'src/utils/excelValidation';

interface ShipmentDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type MappingRow = {
  source: string; // header in the excel file (as-is)
  target: string; // canonical target (core or predefined variable name)
};

const CORE_TARGETS = ['Barcode', 'Quantity', 'Name', 'Symbol', 'MinQuantity', 'MaxQuantity'] as const;
type CoreTarget = typeof CORE_TARGETS[number];

// ---------- helpers ----------
const norm = (s: string) =>
  s?.toString().trim().replace(/\s|_|-/g, '').toLowerCase() ?? '';

const isRequiredTarget = (t: string) => t === 'Barcode' || t === 'Name';

const groupLabel = (value: string, predefinedNames: string[]) => {
  if (CORE_TARGETS.includes(value as CoreTarget)) return 'Core fields';
  if (predefinedNames.includes(value)) return 'Predefined';
  return 'New value';
};

// Core synonyms
const coreSuggestion = (nHeader: string): CoreTarget | '' => {
  if (['barcode', 'barcodes', 'code', 'productcode', 'sku'].includes(nHeader)) return 'Barcode';
  if (['quantity', 'Quantity', 'Qty', 'qty', 'quantities', 'scan'].includes(nHeader)) return 'Quantity';
  if (['name', 'productname', 'title', 'itemname', 'description', 'desc'].includes(nHeader)) return 'Name';
  if (['symbol', 'shortcode', 'stylenumber', 'styleno', 'style', 'style#', 'stylecode'].includes(nHeader)) return 'Symbol';
  if (['minquantity', 'minqty', 'min', 'minstock'].includes(nHeader)) return 'MinQuantity';
  if (['maxquantity', 'maxqty', 'max', 'maxstock'].includes(nHeader)) return 'MaxQuantity';
  return '';
};

// Predefined synonyms (maps normalized header -> canonical predefined name)
const PREDEFINED_SYNONYMS: Record<string, string> = {
  brand: 'Brand',
  category: 'Category',
  maincategory: 'MainCategory',
  generic: 'Generic',
  colour: 'Color',
  colur: 'Color',
  color: 'Color',
  size: 'size',
  season: 'season',
  stylenumber: 'STYLENUMBER'
};

// Suggest target with **predefined priority**, then core
const suggestTarget = (header: string, predefinedNames: string[]): string => {
  const nh = norm(header);

  // 1) exact match to an existing predefined (normalized)
  const predefinedNormMap: Record<string, string> = {};
  for (const p of predefinedNames) {
    predefinedNormMap[norm(p)] = p;
  }
  if (predefinedNormMap[nh]) return predefinedNormMap[nh];

  // 2) check predefined synonyms (Brand/Color/etc.)
  if (PREDEFINED_SYNONYMS[nh]) {
    const candidate = PREDEFINED_SYNONYMS[nh];
    // only return if present in predefineds; otherwise fall back to core or allow new creation
    if (predefinedNames.includes(candidate)) return candidate;
    // if not present, we'll let it become a "New value" once user confirms
    return candidate;
  }

  // 3) fallback to core
  const core = coreSuggestion(nh);
  if (core) return core;

  // 4) nothing
  return '';
};

// ---------- Header Mapping Dialog ----------
const HeaderMappingDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  excelHeaders: string[];
  predefinedNames: string[];
  onCreatePredefined: (name: string) => Promise<void>;
  onSave: (rows: MappingRow[]) => void;
  initialMappings?: MappingRow[];
  onRefreshPredefined?: () => Promise<void>;
}> = ({
  open,
  onClose,
  excelHeaders,
  predefinedNames,
  onCreatePredefined,
  onSave,
  initialMappings = [],
  onRefreshPredefined
}) => {
  const [rows, setRows] = useState<MappingRow[]>([]);
  const [creatingBusy, setCreatingBusy] = useState(false);
  const [filterUnmappedOnly, setFilterUnmappedOnly] = useState(false);
  const [filterRequiredOnly, setFilterRequiredOnly] = useState(false);

  useEffect(() => {
    if (!open) return;

    if (initialMappings.length) {
      const mapped = new Set(initialMappings.map(m => norm(m.source)));
      const extras = excelHeaders
        .filter(h => !mapped.has(norm(h)))
        .map(h => {
          const suggested = suggestTarget(h, predefinedNames);
          return { source: h, target: suggested || '' } as MappingRow;
        });
      setRows([...initialMappings, ...extras]);
    } else {
      const suggestedRows = excelHeaders.map(h => {
        const suggested = suggestTarget(h, predefinedNames);
        return { source: h, target: suggested || '' } as MappingRow;
      });
      setRows(suggestedRows);
    }
  }, [open, excelHeaders, predefinedNames]);

  const allOptions = useMemo(() => {
    console.log('Building allOptions with predefinedNames:', predefinedNames);
    return [...CORE_TARGETS, ...predefinedNames];
  }, [predefinedNames]);

  const displayRows = useMemo(() => {
    let data = rows;
    if (filterUnmappedOnly) data = data.filter(r => !r.target);
    if (filterRequiredOnly) data = data.filter(r => ['barcode', 'name'].includes(norm(r.target)));
    return data;
  }, [rows, filterUnmappedOnly, filterRequiredOnly]);

  const updateTarget = (idx: number, value: string) => {
    setRows(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], target: value?.trim() ?? '' };
      return next;
    });
  };

  const usedCoreTargets = useMemo(() => {
    const used = new Set<string>();
    rows.forEach(r => {
      if (CORE_TARGETS.includes(r.target as CoreTarget)) used.add(r.target);
    });
    return used;
  }, [rows]);

  const hasRequiredMappings = useMemo(() => {
    const mappedTargets = rows.map(r => r.target);
    return ['Barcode', 'Name', 'Symbol', 'Quantity'].every(required => mappedTargets.includes(required));
  }, [rows]);

  const commitCreationIfNeeded = async () => {
    // Create all newly typed (non-core, non-predefined) unique targets
    // Use normalized comparison to ensure accurate matching
    const newOnes = Array.from(
      new Set(
        rows
          .map(r => r.target.trim())
          .filter(Boolean)
          .filter(t => {
            const isCore = CORE_TARGETS.includes(t as CoreTarget);
            const isPredefined = predefinedNames.some(name => norm(name) === norm(t));
            return !isCore && !isPredefined;
          })
      )
    );

    if (!newOnes.length) return;

    setCreatingBusy(true);
    try {
      for (const name of newOnes) {
        await onCreatePredefined(name);
      }
      toast.success(`${newOnes.length} new predefined value(s) created`);
    } finally {
      setCreatingBusy(false);
    }
  };

  const validateBeforeSave = () => {
    const hasBarcode = rows.some(r => r.target === 'Barcode');
    const hasName = rows.some(r => r.target === 'Name');
    if (!hasBarcode || !hasName) {
      toast.error('Please map at least Barcode and Name.');
      return false;
    }
    for (const ct of CORE_TARGETS) {
      const cnt = rows.filter(r => r.target === ct).length;
      if (cnt > 1) {
        toast.error(`"${ct}" must be mapped to a single header.`);
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateBeforeSave()) return;

    await commitCreationIfNeeded();

    const payload = rows.filter(r => r.source && r.target);
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Map Headers</DialogTitle>
      <DialogContent dividers>

        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filterUnmappedOnly}
                onChange={(_, v) => setFilterUnmappedOnly(v)}
              />
            }
            label={<Typography variant="body2">Show unmapped only</Typography>}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={filterRequiredOnly}
                onChange={(_, v) => setFilterRequiredOnly(v)}
              />
            }
            label={<Typography variant="body2">Show required (Barcode, Name)</Typography>}
          />
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={`Headers: ${rows.length}`} variant="outlined" />
            <Chip
              size="small"
              color={rows.some(r => r.target === 'Barcode') ? 'success' : 'default'}
              label={rows.some(r => r.target === 'Barcode') ? 'Barcode ✓' : 'Barcode'}
              variant="outlined"
            />
            <Chip
              size="small"
              color={rows.some(r => r.target === 'Name') ? 'success' : 'default'}
              label={rows.some(r => r.target === 'Name') ? 'Name ✓' : 'Name'}
              variant="outlined"
            />
          </Stack>
        </Stack>

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '40%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Excel Header</Typography>
                </TableCell>
                <TableCell sx={{ width: '45%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Maps To (Core or Predefined)</Typography>
                </TableCell>
                <TableCell sx={{ width: '15%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>Status</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayRows.map((row, idx) => {
                const realIdx = rows.findIndex(r => r.source === row.source);
                const isCore = CORE_TARGETS.includes(row.target as CoreTarget);
                const isPredef = predefinedNames.some(name => norm(name) === norm(row.target));
                const isNew = row.target && !isCore && !isPredef;

                return (
                  <TableRow key={`${row.source}-${idx}`} hover>
                    <TableCell><Typography variant="body2">{row.source}</Typography></TableCell>
                    <TableCell>
                      <Autocomplete
                        size="small"
                        freeSolo
                        value={row.target || ''}
                        onChange={(_, v) => updateTarget(realIdx, (v as string) || '')}
                        onInputChange={(_, v) => updateTarget(realIdx, v)}
                        options={allOptions}
                        groupBy={(option) => groupLabel(option, predefinedNames)}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Select or type a target (e.g., Barcode, Color, Brand)" />
                        )}
                        renderGroup={(params) => (
                          <li key={params.key}>
                            <Box sx={{ px: 1, py: 0.5, bgcolor: (t) => alpha(t.palette.grey[500], 0.08) }}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {params.group}
                              </Typography>
                            </Box>
                            {params.children}
                          </li>
                        )}
                        isOptionEqualToValue={(opt, val) => norm(opt) === norm(val as string)}
                        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : '')}
                        filterSelectedOptions
                      />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {isCore && <Chip size="small" label="Core" color="primary" variant="outlined" />}
                        {isPredef && <Chip size="small" label="Predefined" color="success" variant="outlined" />}
                        {isNew && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            onClick={async () => {
                              const targetName = row.target;
                              try {
                                setCreatingBusy(true);
                                await onCreatePredefined(targetName);
                                toast.success(`"${targetName}" predefined variable created successfully`);
                                
                                // Refresh predefined names from API while preserving current row assignments
                                await onRefreshPredefined?.();
                                
                                // The predefined values should now include the newly created one
                                // The row will automatically re-evaluate as predefined instead of new
                              } catch (error: any) {
                                toast.error(error.message || `Failed to create "${targetName}"`);
                              } finally {
                                setCreatingBusy(false);
                              }
                            }}
                            disabled={creatingBusy}
                          >
                            Create
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}

              {displayRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No rows to display with current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ flex: 1 }} />
          {creatingBusy && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CircularProgress size={16} />
              <Typography variant="caption">Creating new predefined values…</Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={creatingBusy || !hasRequiredMappings}>
          Save Mapping
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------- Drawer ----------
const ShipmentDrawer: React.FC<ShipmentDrawerProps> = ({ open, onClose, onSuccess }) => {
  const [suppliers, setSuppliers] = useState<LovDictionary>({});
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);

  const [mappingOpen, setMappingOpen] = useState(false);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [predefNames, setPredefNames] = useState<string[]>([]);
  const [headerMappings, setHeaderMappings] = useState<MappingRow[]>([]);
  const [currentFile, setCurrentFile] = useState<File | null>(null);
  const [parsingHeaders, setParsingHeaders] = useState(false);
  
  // Form state preservation
  const [formValues, setFormValues] = useState({
    SupplierId: '',
    ExpectedDate: '',
    ExpectedBoxCount: ''
  });

  useEffect(() => {
    if (open) {
      loadSuppliers();
      loadPredefinedValues();
      // Only clear state if truly starting fresh (not reopening after mapping)
      if (!currentFile) {
        setExcelHeaders([]);
        setHeaderMappings([]);
      }
      setError(null);
    } else {
      // Reset form values when drawer closes
      setFormValues({
        SupplierId: '',
        ExpectedDate: '',
        ExpectedBoxCount: ''
      });
    }
  }, [open, currentFile]);

  const loadSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const suppliersData = await LovService.getLovDictionary('all-suppliers');
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Failed to load suppliers:', error);
      setSuppliers({});
      toast.error('Failed to load suppliers');
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const loadPredefinedValues = async () => {
    try {
      const dict = await LovService.getLovDictionary('all-preDefinedValues');
      const names = Object.values(dict || {}) as string[];
      console.log('Loaded predefined values:', names);
      setPredefNames(names.sort());
    } catch (e) {
      console.error('Failed to load predefined values', e);
      setPredefNames([]);
    }
  };

  const parseHeadersFromExcel = async (file: File) => {
    setParsingHeaders(true);
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const rows: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: false });
      const firstRow = rows?.[0] || [];
      const headers = firstRow.map((h: any) => (h ?? '').toString().trim()).filter(Boolean);
      setExcelHeaders(headers);
      return headers;
    } catch (e) {
      console.error('Failed to parse excel headers', e);
      toast.error('Could not read headers from the uploaded file.');
      setExcelHeaders([]);
      return [];
    } finally {
      setParsingHeaders(false);
    }
  };

  const fields: Record<string, DynamicFieldTypes> = {
    SupplierId: {
      type: DynamicField.SELECT,
      required: true,
      name: 'SupplierId',
      title: 'Supplier',
      items: suppliers,
      value: formValues.SupplierId,
      disabled: loadingSuppliers,
    },
    ExpectedDate: {
      type: DynamicField.DATE,
      required: true,
      name: 'ExpectedDate',
      title: 'Expected Date',
      value: formValues.ExpectedDate,
      disabled: false,
    },
    ExpectedBoxCount: {
      type: DynamicField.NUMBER,
      required: true,
      name: 'ExpectedBoxCount',
      title: 'Expected Box Count',
      value: formValues.ExpectedBoxCount,
      min: 1,
      disabled: false,
    },
    ImplementationFile: {
      type: DynamicField.FILE,
      required: true,
      name: 'ImplementationFile',
      title: 'Upload Excel File',
      value: currentFile || '',
      allowedTypes: [FileType.xls, FileType.xlsx],
      maxSizeInMbs: 10,
      disabled: false
    },
  };

  const handleSubmit = async (data: Record<string, any>) => {
    const fileValue = data.ImplementationFile;
    if (!fileValue || (typeof fileValue !== 'object' && typeof fileValue !== 'string')) {
      setError('Please upload an Excel file.');
      return false;
    }

    if (!excelHeaders.length) {
      setError('Please upload an Excel file with valid headers.');
      return false;
    }

    let headerMappingsPayload = (headerMappings || [])
      .filter(m => m.target && m.source)
      .map(m => ({ source: m.source, target: m.target }));

    if (!headerMappingsPayload.length && excelHeaders.length) {
      const autos = excelHeaders
        .map(h => ({ source: h, target: suggestTarget(h, predefNames) }))
        .filter(m => m.target);
      headerMappingsPayload = autos as any;
    }

      const duplicateCheck = await validateExcelDuplicates(fileValue, headerMappingsPayload, ['Barcode', 'Name']);
  if (!duplicateCheck.ok) {
    setError(duplicateCheck.errors.join('\n'));
    toast.error('Duplicate values found in file.');
    return false;
  }

    const requiredMappings = ['Name', 'Symbol', 'Barcode', 'Quantity'];
    const mappedTargets = headerMappingsPayload.map(m => m.target);
    const missingMappings = requiredMappings.filter(req => !mappedTargets.includes(req));
    
    if (missingMappings.length > 0) {
      setError(`Missing required mappings: ${missingMappings.join(', ')}.`);
      return false;
    }

    const invalidMappings = headerMappingsPayload.filter(mapping => 
      requiredMappings.includes(mapping.target) && !excelHeaders.includes(mapping.source)
    );
    
    if (invalidMappings.length > 0) {
      setError(`Invalid mappings detected. Some required fields are mapped to headers that don't exist in the Excel file. Please check your mappings.`);
      return false;
    }

    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('SupplierId', data.SupplierId);
      formData.append('ExpectedDate', data.ExpectedDate);
      formData.append('ExpectedBoxCount', String(data.ExpectedBoxCount));
      formData.append('FormFile', fileValue);
      if (headerMappingsPayload.length) {
        formData.append('HeaderMappingsJson', JSON.stringify(headerMappingsPayload));
      }

      const res = await DataService.postForm('api/shipment', formData);

      if (res.ok) {
        toast.success('Shipment created!');
        onSuccess();
        onClose();
        return true;
      } else {
        const errData = await res.json();
        setError(errData?.message || 'Failed to create shipment');
        toast.error(errData?.message || 'Failed to create shipment');
        return false;
      }
    } catch (err: any) {
      setError('Failed to create shipment');
      toast.error('Failed to create shipment');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const canMapHeaders = !!currentFile && excelHeaders.length > 0 && !parsingHeaders;

  const handleOpenMapping = () => {
    if (!canMapHeaders) {
      toast.info('Please upload an Excel file first.');
      return;
    }
    setMappingOpen(true);
  };

  const handleSaveMapping = (rows: MappingRow[]) => {
    setHeaderMappings(rows);
    setMappingOpen(false);
    toast.success('Header mapping saved');
  };

  const handleCreatePredefined = async (name: string) => {
    const res = await DataService.post('api/PreDefinedVariable/create', { Name: name });
    if (!res?.ok) {
      const msg = (await res?.json())?.message || 'Failed to create predefined variable';
      throw new Error(msg);
    }
    
    // Update predefined list locally immediately to prevent duplicate creation attempts
    setPredefNames(prev => {
      const normalizedExisting = prev.map(p => norm(p));
      if (normalizedExisting.includes(norm(name))) return prev;
      const next = [...prev, name];
      next.sort();
      return next;
    });
  };

  if (loading) {
    return (
      <GenericDialog title="Add Shipment" open={open} onClose={onClose}>
        <Loader
          variant="section"
          type="warehouse"
          size="medium"
          message="Creating shipment..."
          subMessage="Please wait while we process your request"
          showProgress={true}
        />
      </GenericDialog>
    );
  }

  return (
    <GenericDialog title="Add Shipment" open={open} onClose={onClose}>
      {error && (
        <div style={{
          background: '#fdecea',
          color: '#b71c1c',
          padding: 12,
          borderRadius: 8,
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          position: 'relative'
        }}>
          <span style={{ flex: 1 }}>{error}</span>
          <span style={{ cursor: 'pointer', marginLeft: 8 }} onClick={() => setError(null)}>
            <CloseIcon fontSize="small" />
          </span>
        </div>
      )}

      {/* File helper row */}
      <Box sx={{ mb: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Chip
          icon={<UploadFileIcon />}
          label={currentFile ? currentFile.name : 'No file selected'}
          variant="outlined"
          size="small"
        />
        <Tooltip title={canMapHeaders ? 'Open header mapping' : 'Upload an Excel file to map headers'}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<MapIcon />}
              disabled={!canMapHeaders}
              onClick={handleOpenMapping}
            >
              Map headers
            </Button>
          </span>
        </Tooltip>
        {parsingHeaders && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="caption">Reading headers…</Typography>
          </Stack>
        )}
      </Box>

      {loadingSuppliers ? (
        <div style={{
          minHeight: '200px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          marginBottom: '16px'
        }}>
          <Loader
            variant="compact"
            type="scanner"
            size="small"
            message="Loading suppliers..."
            subMessage="Fetching available suppliers"
            showProgress={true}
          />
        </div>
      ) : (
        <DynamicFormWidget
          title=""
          drawerMode
          fields={fields}
          onSubmit={handleSubmit}
          initialValues={formValues}
          onFieldChange={async (name, value) => {
            if (name === 'ImplementationFile') {
              const file = value instanceof File ? value : null;
              setCurrentFile(file);
              if (file) await parseHeadersFromExcel(file);
              else setExcelHeaders([]);
            } else {
              // Preserve other form field values
              setFormValues(prev => ({ ...prev, [name]: value }));
            }
          }}
        />
      )}

      <HeaderMappingDialog
        open={mappingOpen}
        onClose={() => setMappingOpen(false)}
        excelHeaders={excelHeaders}
        predefinedNames={predefNames}
        onCreatePredefined={handleCreatePredefined}
        onSave={handleSaveMapping}
        initialMappings={headerMappings}
        onRefreshPredefined={loadPredefinedValues}
      />
    </GenericDialog>
  );
};

export default ShipmentDrawer;
